import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, AlertTriangle, X, Plus, Clock, CheckCheck } from 'lucide-react';
import PageSkeleton from '../common/Skeleton';
import DeviceAutocomplete from '../common/DeviceAutocomplete';
import Modal from '../common/ui/Modal';
import {
  Button,
  AlertMessage,
  PageHeader
} from '../common/ui';
import { useOrderItems } from '../../hooks/useOrderItems';
import { groupByProduct, generateSerialSlots } from '../../utils/orderUtils';
import { SerialSlot, ProductGroup } from '../../types/orders';
import { normalizeSerialNumber, validateSerialNumber } from '../../utils/normalizeSerialNumber';
import { formatDate } from '../../utils/dateUtils';

const OrderSerialNumbers: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [success, setSuccess] = useState('');
  const [serialInputs, setSerialInputs] = useState<Record<string, string>>({});
  const [selectedDevices, setSelectedDevices] = useState<Record<string, { id: number; product_id: number | null }>>({});
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const shouldFocusRef = useRef(false);
  
  const {
    order,
    orderItems,
    loading,
    error,
    validating,
    validationErrors,
    assignSerial,
    batchAssignSerials,
    unassignSerialNumber,
    clearValidationError,
    setError
  } = useOrderItems(orderId);

  const getAllSlots = useCallback((): SerialSlot[] => {
    return groupByProduct(orderItems).flatMap(group => generateSerialSlots(group));
  }, [orderItems]);

  const focusFirstEmptySlot = useCallback((slots?: SerialSlot[]) => {
    const allSlots = slots || getAllSlots();
    for (const slot of allSlots) {
      if (!serialInputs[slot.slotId]?.trim()) {
        const el = inputRefs.current[slot.slotId];
        if (el) {
          el.focus();
          return;
        }
      }
    }
  }, [getAllSlots, serialInputs]);

  // Auto-focus after initial load
  useEffect(() => {
    if (!loading && orderItems.length > 0) {
      shouldFocusRef.current = true;
    }
  }, [loading, orderItems]);

  // Focus when shouldFocusRef is set (after render so refs are attached)
  useEffect(() => {
    if (shouldFocusRef.current) {
      shouldFocusRef.current = false;
      requestAnimationFrame(() => focusFirstEmptySlot());
    }
  });

  const handleSerialInput = (slotId: string, value: string, options?: { preserveSelected?: boolean }) => {
    setSerialInputs(prev => ({
      ...prev,
      [slotId]: value
    }));
    
    if (!options?.preserveSelected && selectedDevices[slotId]) {
      setSelectedDevices(prev => ({
        ...prev,
        [slotId]: undefined as any
      }));
    }
    
    if (validationErrors[slotId]) {
      clearValidationError(slotId);
    }
  };

  const validateAndAssignSerial = async (
    slot: SerialSlot,
    selectedOverride?: { id: number; product_id: number | null },
    serialOverride?: string
  ) => {
    const serialNumber = normalizeSerialNumber(serialOverride ?? serialInputs[slot.slotId] ?? '') || '';
    
    if (!serialNumber) {
      return;
    }

    const serialCheck = validateSerialNumber(serialNumber);
    if (!serialCheck.valid) {
      setError(serialCheck.error || 'Invalid serial number');
      return;
    }

    if (!orderId) {
      return;
    }

    const selectedDevice = selectedOverride || selectedDevices[slot.slotId];

    const result = await assignSerial(
      slot.itemId,
      selectedDevice?.id,
      selectedDevice ? undefined : serialNumber,
      slot.slotId
    );

    if (result) {
      setSuccess(`Serial number ${serialNumber} assigned successfully`);
      setSerialInputs(prev => ({ ...prev, [slot.slotId]: '' }));
      if (selectedDevices[slot.slotId]) {
        setSelectedDevices(prev => ({ ...prev, [slot.slotId]: undefined as any }));
      }
      setTimeout(() => setSuccess(''), 3000);
      shouldFocusRef.current = true;
    }
  };

  const handleAddAll = async (group: ProductGroup) => {
    if (!orderId) return;

    const slots = generateSerialSlots(group);
    const filledSlots = slots.filter(slot => serialInputs[slot.slotId]?.trim());
    if (filledSlots.length === 0) return;

    // Client-side validation first
    for (const slot of filledSlots) {
      const serialNumber = normalizeSerialNumber(serialInputs[slot.slotId] || '');
      if (!serialNumber) continue;
      const check = validateSerialNumber(serialNumber);
      if (!check.valid) {
        setError(check.error || 'Invalid serial number');
        return;
      }
    }

    const assignments = filledSlots.map(slot => {
      const selectedDevice = selectedDevices[slot.slotId];
      const serialNumber = normalizeSerialNumber(serialInputs[slot.slotId] || '') || '';
      return {
        orderItemId: slot.itemId,
        deviceId: selectedDevice?.id,
        serialNumber: selectedDevice ? undefined : serialNumber,
        slotId: slot.slotId
      };
    });

    setBatchProgress({ current: 0, total: assignments.length });

    const results = await batchAssignSerials(assignments);

    setBatchProgress(null);

    const successCount = Object.values(results).filter(r => r.success).length;
    const failCount = Object.values(results).filter(r => !r.success).length;

    // Clear inputs and selected devices for successful slots
    setSerialInputs(prev => {
      const next = { ...prev };
      Object.entries(results).forEach(([slotId, r]) => {
        if (r.success) next[slotId] = '';
      });
      return next;
    });
    setSelectedDevices(prev => {
      const next = { ...prev };
      Object.entries(results).forEach(([slotId, r]) => {
        if (r.success) delete next[slotId];
      });
      return next;
    });

    if (successCount > 0 && failCount === 0) {
      setSuccess(`${successCount} serial number${successCount > 1 ? 's' : ''} assigned successfully`);
    } else if (successCount > 0 && failCount > 0) {
      setSuccess(`${successCount} assigned, ${failCount} failed`);
    }
    if (successCount > 0) {
      setTimeout(() => setSuccess(''), 3000);
    }

    shouldFocusRef.current = true;
  };

  // Unassign modal state
  const [unassignModal, setUnassignModal] = useState<{ assignmentId: number; serialNumber: string } | null>(null);
  const [unassignMode, setUnassignMode] = useState<'returned' | 'hard_delete' | ''>('');
  const [hardDeleteConfirmed, setHardDeleteConfirmed] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  const openUnassignModal = (assignmentId: number, serialNumber: string) => {
    setUnassignModal({ assignmentId, serialNumber });
    setUnassignMode('');
    setHardDeleteConfirmed(false);
  };

  const closeUnassignModal = () => {
    setUnassignModal(null);
    setUnassignMode('');
    setHardDeleteConfirmed(false);
  };

  const handleUnassignConfirm = async () => {
    if (!unassignModal || !unassignMode) return;
    if (unassignMode === 'hard_delete' && !hardDeleteConfirmed) return;
    setIsUnassigning(true);
    try {
      const isHardDelete = unassignMode === 'hard_delete';
      const result = await unassignSerialNumber(
        unassignModal.assignmentId,
        unassignModal.serialNumber,
        isHardDelete ? undefined : 'Returned',
        isHardDelete
      );
      if (result) {
        setSuccess(`Serial number ${unassignModal.serialNumber} unassigned successfully`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } finally {
      setIsUnassigning(false);
      closeUnassignModal();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, slot: SerialSlot) => {
    if (e.key === 'Enter') {
      validateAndAssignSerial(slot);
    }
  };

  // Cleanup stale state after orderItems change
  useEffect(() => {
    if (loading || orderItems.length === 0) return;
    const currentSlotIds = new Set(getAllSlots().map(s => s.slotId));
    setSerialInputs(prev => {
      const staleKeys = Object.keys(prev).filter(k => !currentSlotIds.has(k));
      if (staleKeys.length === 0) return prev;
      const next = { ...prev };
      staleKeys.forEach(k => delete next[k]);
      return next;
    });
    setSelectedDevices(prev => {
      const staleKeys = Object.keys(prev).filter(k => !currentSlotIds.has(k));
      if (staleKeys.length === 0) return prev;
      const next = { ...prev };
      staleKeys.forEach(k => delete next[k]);
      return next;
    });
  }, [orderItems, loading, getAllSlots]);

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Serial Number Assignment</h1>
        <PageSkeleton rows={3} rowHeight="h-32" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Order Not Found</h3>
          <Button variant="secondary" onClick={() => navigate('/orders')} icon={undefined}>
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title={
          <>
            Order{' '}
            <a
              href={`https://b2b.naviter.com/wp-admin/post.php?post=${order.order_number}&action=edit`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-naviter-blue hover:underline"
            >
              #{order.order_number}
            </a>
          </>
        }
        subtitle={`${order.customer_name} - ${order.customer_email}`}
        onBack={() => navigate(-1)}
        reserveWidthPx={0}
        actions={<></>}
      />

      {/* Success/Error Messages */}
      {error && <AlertMessage type="error" message={error} title="" onDismiss={() => setError('')} dismissible>{''}</AlertMessage>}
      {success && <AlertMessage type="success" message={success} title="" onDismiss={() => setSuccess('')} dismissible>{''}</AlertMessage>}

      {/* No eligible items */}
      {orderItems.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-6 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-600 mb-4" />
          <h3 className="text-lg font-medium text-yellow-900 mb-2">No Eligible Products</h3>
          <p className="text-sm text-yellow-700 mb-4">
            This order doesn't contain any products that are linked to your local product database.
          </p>
          <Button
            variant="secondary"
            onClick={() => navigate('/orders/settings')}
            icon={undefined}
          >
            Go to Product Mapping
          </Button>
        </div>
      )}

      {/* Product Groups */}
      <div className="space-y-6">
        {groupByProduct(orderItems).map((group) => {
          const isFullyAssigned = group.totalAssigned >= group.totalQuantity;
          
          return (
            <div key={group.productId} className="bg-white border border-gray-200 rounded-lg shadow-sm">
              {/* Product Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{group.productName}</h3>
                    {group.b2bProductName !== group.productName && (
                      <p className="text-sm text-gray-500 mt-1">B2B: {group.b2bProductName}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      isFullyAssigned ? 'text-green-600' : 'text-gray-900'
                    }`}>
                      {group.totalAssigned}/{group.totalQuantity}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {isFullyAssigned ? 'Complete' : 'Assigned'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Serial Input Fields */}
              {!isFullyAssigned ? (() => {
                const slots = generateSerialSlots(group);
                const filledCount = slots.filter(s => serialInputs[s.slotId]?.trim()).length;
                const isAnyValidating = slots.some(s => validating[s.slotId]);
                return (
                  <div className="px-6 py-4 border-b border-gray-200 space-y-4">
                    {slots.map((slot, index) => {
                      const slotError = validationErrors[slot.slotId];
                      const isSlotValidating = validating[slot.slotId];
                      
                      return (
                        <div key={slot.slotId} className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Enter Serial Number {slots.length > 1 ? `(${index + 1} of ${slots.length})` : ''}
                          </label>
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <DeviceAutocomplete
                              ref={(el) => { inputRefs.current[slot.slotId] = el; }}
                              value={serialInputs[slot.slotId] || ''}
                              onChange={(value) => handleSerialInput(slot.slotId, value)}
                              onDeviceSelect={(device) => {
                                if (device) {
                                  setSelectedDevices(prev => ({
                                    ...prev,
                                    [slot.slotId]: {
                                      id: device.id,
                                      product_id: device.product_id || null
                                    }
                                  }));
                                  handleSerialInput(slot.slotId, device.serial_number, { preserveSelected: true });
                                }
                              }}
                              onKeyPress={(e: any) => handleKeyPress(e, slot)}
                              placeholder="Type or scan serial number..."
                              required={false}
                              disabled={isSlotValidating}
                            />
                          </div>
                          <Button
                            variant="primary"
                            onClick={() => validateAndAssignSerial(slot)}
                            disabled={!serialInputs[slot.slotId]?.trim() || isSlotValidating}
                            icon={Plus}
                          >
                            {isSlotValidating ? 'Adding...' : 'Add'}
                          </Button>
                        </div>
                        {slotError && (() => {
                          const errorMessage = typeof slotError === 'string' ? slotError : slotError.message;
                          const existingOrderId = typeof slotError === 'object' ? slotError.existingOrderId : undefined;
                          const existingOrderNumber = typeof slotError === 'object' ? slotError.existingOrderNumber : undefined;
                          
                          let errorContent: React.ReactNode = errorMessage;
                          if (existingOrderId && existingOrderNumber) {
                            const orderNumberRegex = new RegExp(`order ${existingOrderNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
                            const match = errorMessage.match(orderNumberRegex);
                            
                            if (match) {
                              const beforeOrder = errorMessage.substring(0, match.index);
                              const afterOrder = errorMessage.substring((match.index || 0) + match[0].length);
                              
                              errorContent = (
                                <>
                                  {beforeOrder}
                                  order{' '}
                                  <button
                                    onClick={() => navigate(`/orders/${existingOrderId}/serials`)}
                                    className="underline hover:text-red-800 font-medium"
                                  >
                                    {existingOrderNumber}
                                  </button>
                                  {afterOrder}
                                </>
                              );
                            }
                          }
                          
                          return (
                            <div className="mt-2 flex items-start text-sm text-red-600">
                              <AlertTriangle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                              <span>{errorContent}</span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                    {filledCount > 0 && slots.length > 1 && (
                      <div className="pt-2">
                        <Button
                          variant="primary"
                          onClick={() => handleAddAll(group)}
                          disabled={isAnyValidating || !!batchProgress}
                          icon={CheckCheck}
                        >
                          {batchProgress
                            ? `Adding ${batchProgress.current} of ${batchProgress.total}...`
                            : `Add All (${filledCount})`}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })() : (
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span className="font-medium">All serial numbers assigned</span>
                  </div>
                </div>
              )}

              {/* Assigned Serial Numbers */}
              {group.allAssignments.length > 0 && (
                <div className="px-6 py-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Assigned Serial Numbers</h4>
                  <div className="space-y-2">
                    {group.allAssignments.map((assignment) => (
                      <div
                        key={assignment.assignment_id}
                        className="flex items-center justify-between py-2 px-3 bg-green-50 border border-green-200 rounded-md"
                      >
                        <div className="flex items-center">
                          <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                          <a
                            href={`#/devices/id/${assignment.device_id}/view`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/devices/id/${assignment.device_id}/view`);
                            }}
                            className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {assignment.serial_number}
                          </a>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openUnassignModal(assignment.assignment_id, assignment.serial_number)}
                          icon={X}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Past Assignments (unassigned devices) */}
              {group.allPastAssignments.length > 0 && (
                <div className="px-6 py-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-400 mb-3">Previously Assigned</h4>
                  <div className="space-y-2">
                    {group.allPastAssignments.map((past) => (
                      <div
                        key={`past-${past.assignment_id}`}
                        className="flex items-center justify-between py-2 px-3 bg-gray-50 border border-gray-200 rounded-md"
                      >
                        <div className="flex items-center min-w-0">
                          <Clock className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          <a
                            href={`#/devices/id/${past.device_id}/view`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/devices/id/${past.device_id}/view`);
                            }}
                            className="font-mono text-sm font-medium text-gray-500 hover:text-blue-600 hover:underline"
                          >
                            {past.serial_number}
                          </a>
                          {past.unassignment_reason && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                              {past.unassignment_reason}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 whitespace-nowrap ml-2">
                          {past.assigned_at ? formatDate(past.assigned_at) : '?'}
                          {' \u2192 '}
                          {past.unassigned_at ? formatDate(past.unassigned_at) : '?'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Unassign Confirmation Modal */}
      <Modal
        isOpen={!!unassignModal}
        onClose={closeUnassignModal}
        title="Remove Serial Number"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Unassign <span className="font-mono font-medium text-gray-900">{unassignModal?.serialNumber}</span> from this order?
          </p>

          {/* Option buttons */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => { setUnassignMode('returned'); setHardDeleteConfirmed(false); }}
              className={`w-full text-left px-4 py-3 rounded-md border-2 text-sm transition-colors ${
                unassignMode === 'returned'
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <span className="font-medium">Returned</span>
              <p className="text-xs mt-0.5 text-gray-500">Device returned. Assignment history is preserved.</p>
            </button>
            <button
              type="button"
              onClick={() => { setUnassignMode('hard_delete'); setHardDeleteConfirmed(false); }}
              className={`w-full text-left px-4 py-3 rounded-md border-2 text-sm transition-colors ${
                unassignMode === 'hard_delete'
                  ? 'border-red-500 bg-red-50 text-red-900'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <span className="font-medium">Remove (without history record)</span>
              <p className="text-xs mt-0.5 text-gray-500">Permanently removes the assignment. No history is kept.</p>
            </button>
          </div>

          {/* Double-confirm for hard delete */}
          {unassignMode === 'hard_delete' && (
            <label className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md cursor-pointer">
              <input
                type="checkbox"
                checked={hardDeleteConfirmed}
                onChange={(e) => setHardDeleteConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-xs text-red-800">
                I confirm this will permanently remove the assignment with no history record.
              </span>
            </label>
          )}

          <div className="flex justify-end space-x-3 pt-2">
            <button
              onClick={closeUnassignModal}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
              disabled={isUnassigning}
            >
              Cancel
            </button>
            <button
              onClick={handleUnassignConfirm}
              disabled={isUnassigning || !unassignMode || (unassignMode === 'hard_delete' && !hardDeleteConfirmed)}
              className={`px-4 py-2 rounded-md text-white text-sm disabled:opacity-50 ${
                unassignMode === 'hard_delete'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isUnassigning ? 'Removing...' : unassignMode === 'hard_delete' ? 'Remove permanently' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrderSerialNumbers;


